import { AnyAction, Dispatch } from 'redux';
import { actions } from '@auth/_redux';
import { getUserMfaSettings } from '@auth/_redux/authCrud';
import { LoginAuthStep } from '@auth/pages/enums';
import { INotification } from '@components/types';
import { GetLocaleDate, Translate } from '@hooks/useI18n';
import { ICommonMfaSettings } from '@schemas/common/types/mfaSettings';
import { AlertSeverity } from '@utils/enums';
import { HandleStepChange } from '../AuthForm/types';
import { get24HourTime } from './get24HourTime';
import { isDatePastEnforcementDate } from './isDatePastEnforcementDate';

interface IGetUserMfaSettings {
  accessToken: string;
  dispatch: Dispatch<AnyAction>;
  getLocaleDate: GetLocaleDate;
  handleStepChange: HandleStepChange;
  translate: Translate;
}

export interface IGetUserMfaSettingsResponse {
  data: ICommonMfaSettings;
}

export const updateStepFromUserMfaSettings = async ({
  accessToken,
  dispatch,
  getLocaleDate,
  handleStepChange,
  translate,
}: IGetUserMfaSettings) => {
  const { data }: IGetUserMfaSettingsResponse = await getUserMfaSettings();

  const {
    mfa_available: isMfaAvailable,
    mfa_enforced: isMfaEnforced,
    mfa_enforcement_banner_date: mfaEnforcementBannerDate,
    mfa_enrolled: isMfaEnrolled,
    totp_url: totpUrl,
  } = data;

  if (!isMfaAvailable) {
    dispatch(actions.login(accessToken));
  } else {
    const isSkippable = !isMfaEnforced;
    if (isMfaEnrolled) {
      handleStepChange({
        step: LoginAuthStep.Step2MfaCodeEntryPage,
        accessToken,
        options: { isSkippable },
      });
    } else {
      handleStepChange({
        step: LoginAuthStep.Step3MfaSetupPage,
        accessToken,
        options: {
          totpUrlString: totpUrl,
          isSkippable,
        },
      });
    }
  }

  // TODO: Remove once MFA rollout is complete - https://heidi-pay.atlassian.net/browse/HC-16435
  if (
    mfaEnforcementBannerDate &&
    !isDatePastEnforcementDate(new Date(mfaEnforcementBannerDate))
  ) {
    const notification: INotification = {
      isClosable: true,
      message: translate('OPS_PORTAL.NOTIFICATION.MFA_ENFORCEMENT', {
        date: `${getLocaleDate({
          date: mfaEnforcementBannerDate,
        })} ${get24HourTime(new Date(mfaEnforcementBannerDate))}`,
      }),
      severity: AlertSeverity.Warning,
    };
    dispatch(actions.setGlobalBannerNotification(notification));
  }
};
